import { useState } from 'react';

import {
  ListItemIcon,
  ListItemText,
  MenuItem,
  MenuList,
} from '@material-ui/core';

import {
  RiCalendarLine as CalendarToday,
  RiCheckLine as Check,
} from '@remixicon/react';
import {
  format,
  isSameDay,
  isSameWeek,
  isSameYear,
  startOfDay,
  subDays,
} from 'date-fns';

import { DateUtils } from '../../utils';
import { formatDateRange } from '../../utils/format-date-range';
import { DateRangePicker } from './DateRangePicker';
import { FilterChip } from './FilterChip';
import {
  FilterEditorComponentProps,
  FilterMeta,
  FilterViewerComponentProps,
  createFilter,
} from './provider';

export interface FilterDateRange {
  start?: string | undefined;
  end?: string | undefined;
  relative?: boolean;
  custom?: boolean;
}

const formatDate = (date: Date) => {
  const today = startOfDay(new Date());
  const sameDay = isSameDay(date, today);
  const sameWeek = isSameWeek(date, today);
  const sameYear = isSameYear(date, today);

  if (sameDay) {
    return 'today';
  }

  if (sameWeek) {
    return format(date, 'EEEE');
  }

  if (sameYear) {
    return format(date, 'do MMM');
  }

  return format(date, 'Lo MMM, yyyy');
};

export const FilterDateRangeViewer = (
  props: FilterViewerComponentProps<FilterDateRange>,
) => {
  const { value, filter } = props;
  const today = startOfDay(new Date());
  let label = '';

  if (value.relative) {
    if (value.start) {
      label = `${filter.label} ${DateUtils.xAgo(new Date(value.start), today)}`;
    } else if (value.end) {
      label = `${filter.label} before ${formatDate(new Date(value.end))}`;
    }
  } else {
    if (value.start && value.end) {
      label = `${filter.label} during ${formatDateRange(
        new Date(value.start),
        new Date(value.end),
      )}`;
    } else if (value.start) {
      label = `${filter.label} after ${formatDate(new Date(value.start))}`;
    } else if (value.end) {
      label = `${filter.label} before ${formatDate(new Date(value.end))}`;
    }
  }

  return <FilterChip {...props} label={label} />;
};

const RELATIVE_OPTIONS = [
  {
    label: '1 day ago',
    relativeDays: 1,
  },
  {
    label: '3 days ago',
    relativeDays: 3,
  },
  {
    label: '1 week ago',
    relativeDays: 7,
  },
  {
    label: '1 month ago',
    relativeDays: 30,
  },
  {
    label: '6 months ago',
    relativeDays: 180,
  },
  {
    label: '1 year ago',
    relativeDays: 365,
  },
];

export const dateRangeQuery = (field: string, value: FilterDateRange) => {
  if (value.start && value.end) {
    return `${field}:[${value.start} TO ${value.end}]`;
  }

  if (value.start) {
    return `${field}:[${value.start} TO *]`;
  }

  if (value.end) {
    return `${field}:[* TO ${value.end}]`;
  }

  return '';
};

export const FilterDateRangeEditor = (
  props: FilterEditorComponentProps<FilterDateRange>,
) => {
  const { value, onValueChange, onClose } = props;
  const [showCustom, setShowCustom] = useState(value?.custom ?? false);

  if (showCustom) {
    return (
      <DateRangePicker
        value={value || {}}
        onChange={v => {
          onValueChange({
            ...v,
            custom: true,
          });
          setTimeout(() => {
            onClose();
          }, 0);
        }}
        onClose={onClose}
       data-track-source-anchor="b98c964d92;filter-date-range-editor-date-range-picker"/>
    );
  }

  const today = startOfDay(new Date());

  return (
    <MenuList autoFocus>
      {RELATIVE_OPTIONS.map(option => (
        <MenuItem
          key={option.label}
          dense
          button
          onClick={() => {
            onValueChange({
              relative: true,
              start: subDays(today, option.relativeDays).toISOString(),
            });
            onClose();
          }}
         data-track-source-anchor="3a48afc43e;filter-date-range-editor-menu-item;low">
          {value !== null && (
            <ListItemIcon>
              {value?.start ===
              subDays(today, option.relativeDays).toISOString() ? (
                <Check />
              ) : null}
            </ListItemIcon>
          )}
          <ListItemText primary={option.label} />
        </MenuItem>
      ))}

      <MenuItem dense button onClick={() => setShowCustom(true)} data-track-source-anchor="1b55f94cb5;filter-date-range-editor-menu-item-2;low">
        {value !== null && (
          <ListItemIcon>
            <CalendarToday size={20} />
          </ListItemIcon>
        )}
        <ListItemText primary="Custom date or timeframe..." />
      </MenuItem>
    </MenuList>
  );
};

interface DateRangeFilterOptions extends FilterMeta {
  field: string;
}

export const createDateRangeFilter = (options: DateRangeFilterOptions) => {
  const { field, ...filterMeta } = options;

  return createFilter<FilterDateRange>({
    ...filterMeta,
    query: (value: FilterDateRange) => dateRangeQuery(field, value),
    viewer: FilterDateRangeViewer,
    editor: FilterDateRangeEditor,
  });
};
